import { AlertCircle, CheckCircle2, Upload, X } from 'lucide-react';
import { formatFileSize } from '@/lib/utils';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import type { UploadStageProps } from './types';

export function UploadStage({
  selectedFile,
  description,
  isValidating,
  error,
  dragActive,
  fileInputRef,
  onFileChange,
  onDrag,
  onDrop,
  onDescriptionChange,
  onClearFile,
}: UploadStageProps) {
  return (
    <div className="space-y-4">
      {/* Drag-and-drop zone */}
      {/* biome-ignore lint/a11y/noStaticElementInteractions: Drop zone for drag-and-drop file upload */}
      <div
        className={`border-2 border-dashed p-8 text-center transition-colors ${
          dragActive
            ? 'border-primary bg-primary/5'
            : error
              ? 'border-red-300 bg-red-50'
              : 'border-gray-300 hover:border-gray-400'
        }`}
        onDragEnter={onDrag}
        onDragLeave={onDrag}
        onDragOver={onDrag}
        onDrop={onDrop}
      >
        <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
        <div className="space-y-2">
          <p className="text-sm font-medium">
            {selectedFile ? selectedFile.name : 'Drag and drop your payment file here'}
          </p>
          <p className="text-xs text-muted-foreground">
            {selectedFile
              ? `${formatFileSize(selectedFile.size)} • ${selectedFile.type || 'text/csv'}`
              : 'or click below to browse'}
          </p>
        </div>
      </div>

      {/* File input */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={isValidating}
            className="shrink-0"
          >
            Choose File
          </Button>
          <span className="text-sm text-muted-foreground truncate flex-1">
            {selectedFile ? selectedFile.name : 'No file chosen'}
          </span>
          {selectedFile && !isValidating && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={onClearFile}
              className="shrink-0"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          onChange={onFileChange}
          accept=".csv,.txt"
          disabled={isValidating}
          className="hidden"
        />
        <p className="text-xs text-muted-foreground">Supported formats: CSV, TXT</p>
      </div>

      {/* Description field */}
      <div className="space-y-2">
        <Label htmlFor="description">Description (Optional)</Label>
        <Textarea
          id="description"
          placeholder="Add notes about this upload..."
          value={description}
          onChange={(e) => onDescriptionChange(e.target.value)}
          disabled={isValidating}
          rows={3}
        />
      </div>

      {/* Error message */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md flex items-start gap-2">
          <AlertCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-800">Validation failed</p>
            <p className="text-sm text-red-700">{error.message}</p>
          </div>
        </div>
      )}

      {/* Success indicator (file selected, no errors) */}
      {selectedFile && !error && !isValidating && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-md flex items-center gap-2">
          <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
          <p className="text-sm text-green-700">File ready for validation</p>
        </div>
      )}

      {/* Validation in progress */}
      {isValidating && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
          <div className="flex items-center gap-2 mb-2">
            <div className="h-2 w-2 bg-blue-600 rounded-full animate-pulse" />
            <p className="text-sm font-medium text-blue-800">Validating...</p>
          </div>
          <p className="text-xs text-blue-700">
            Checking file structure and validating data against payment schema...
          </p>
        </div>
      )}
    </div>
  );
}
